import * as React from 'react';
import { useEffect } from 'react';

import { format, isValid, parse } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy';

interface Props {
    date: Date | undefined;
    onDateChange: (date: Date | undefined) => void;
    className?: string;
    /** Extra classes for the input itself, e.g. to match sibling text inputs. */
    inputClassName?: string;
    testId?: string;
    isError?: boolean;
    /** date-fns format string for display/parsing. Defaults to `dd/MM/yyyy`. */
    dateFormat?: string;
    /** Extra attributes for the input, e.g. an `id` for a label or a grid-cell marker. */
    inputProps?: Omit<
        React.ComponentPropsWithoutRef<typeof Input>,
        'value' | 'onChange' | 'onBlur' | 'onClick' | 'placeholder'
    >;
}

export function DatePicker({
    date,
    onDateChange,
    className,
    inputClassName,
    testId = 'DatePicker',
    isError = false,
    dateFormat = DEFAULT_DATE_FORMAT,
    inputProps,
}: Props) {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(
        date ? format(date, dateFormat) : '',
    );

    useEffect(() => {
        setInputValue(date ? format(date, dateFormat) : '');
    }, [date, dateFormat]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        if (relatedTarget?.closest('[role="dialog"]')) {
            // Focus is moving into the calendar popover.
            // Let handleCalendarSelect handle the date change.
            return;
        }
        const trimmed = inputValue.trim();
        if (trimmed === '') {
            onDateChange(undefined);
            return;
        }
        const parsed = parse(trimmed, dateFormat, new Date());
        if (isValid(parsed)) {
            onDateChange(parsed);
        } else {
            // Revert to last valid value
            setInputValue(date ? format(date, dateFormat) : '');
        }
    };

    const handleCalendarSelect = (selected: Date | undefined) => {
        onDateChange(selected);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <div
                className={`relative flex w-[280px] items-center ${className ?? ''}`}
            >
                <Input
                    {...inputProps}
                    data-testid={testId}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onClick={() => setOpen(true)}
                    placeholder={dateFormat.toUpperCase()}
                    aria-invalid={isError || undefined}
                    className={cn(
                        'pr-10',
                        inputClassName,
                        isError &&
                            'border-destructive focus-visible:ring-destructive',
                    )}
                />
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 h-7 w-7 text-muted-foreground hover:text-foreground"
                        aria-label="Open calendar"
                    >
                        <CalendarIcon className="h-4 w-4" />
                    </Button>
                </PopoverTrigger>
            </div>
            <PopoverContent
                className="w-auto p-0"
                data-testid={`${testId}Popover`}
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <Calendar
                    mode="single"
                    selected={date}
                    onSelect={handleCalendarSelect}
                />
            </PopoverContent>
        </Popover>
    );
}
